import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/auth/LoginPage';
import AppLayout from './components/layout/AppLayout';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminEmployeesPage from './pages/AdminEmployeesPage';
import AdminClientsPage from './pages/AdminClientsPage';
import AdminTimesheetsPage from './pages/AdminTimesheetsPage';
import AdminReportsPage from './pages/AdminReportsPage';
import AdminProjectsPage from './pages/AdminProjectsPage';
import AdminActivityLogsPage from './pages/AdminActivityLogsPage';
import AdminExpensesPage from './pages/AdminExpensesPage';
import AdminAdminsPage from './pages/AdminAdminsPage';
import EmployeeTimesheetPage from './pages/EmployeeTimesheetPage';
import CustomerTimesheetsPage from './pages/CustomerTimesheetsPage';

const theme = createTheme({
  palette: {
    primary: { main: '#3B82F6', dark: '#2563EB', light: '#60A5FA' },
    secondary: { main: '#64748B' },
    success: { main: '#10B981' },
    warning: { main: '#F59E0B' },
    error: { main: '#EF4444' },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0F172A',
      secondary: '#64748B',
    },
    divider: '#E2E8F0',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: { fontWeight: 600, letterSpacing: '-0.02em', color: '#0F172A' },
    h6: { fontWeight: 600, letterSpacing: '-0.01em', color: '#0F172A' },
    subtitle1: { fontWeight: 500, color: '#334155' },
    subtitle2: { fontWeight: 600, color: '#334155' },
    body1: { color: '#334155' },
    body2: { color: '#64748B' },
    caption: { color: '#94A3B8', fontSize: '0.75rem' },
    button: { textTransform: 'none' as const, fontWeight: 500 },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F8FAFC',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#0F172A',
          borderBottom: '1px solid #E2E8F0',
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: '1px solid #E2E8F0',
          borderRadius: 12,
          transition: 'all 0.2s ease',
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        outlined: {
          borderColor: '#E2E8F0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none' as const,
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          '&:hover': { boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        sizeSmall: {
          height: 24,
          fontSize: '0.6875rem',
          fontWeight: 600,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#E2E8F0',
          fontSize: '0.8125rem',
          padding: '10px 16px',
        },
        head: {
          backgroundColor: '#F0F4FF',
          color: '#475569',
          fontWeight: 600,
          fontSize: '0.6875rem',
          textTransform: 'uppercase' as const,
          letterSpacing: '0.06em',
          lineHeight: 1.2,
          padding: '12px 16px',
          borderBottom: '2px solid #DBEAFE',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(even)': {
            backgroundColor: '#FAFBFD',
          },
          '&:hover': {
            backgroundColor: '#F0F4FF !important',
          },
          transition: 'background-color 0.15s ease',
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          fontSize: 'inherit',
          textTransform: 'inherit' as const,
          letterSpacing: 'inherit',
          fontWeight: 'inherit',
          color: 'inherit',
          '&.Mui-active': {
            color: '#1E40AF',
          },
        },
        icon: {
          fontSize: '0.875rem',
          color: '#94A3B8 !important',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px !important',
          fontSize: '0.8125rem',
          fontWeight: 500,
          padding: '4px 14px',
          textTransform: 'none' as const,
          '&.Mui-selected': {
            backgroundColor: '#EFF6FF',
            color: '#3B82F6',
            borderColor: '#BFDBFE',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#DBEAFE',
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

function ProtectedRoute({ children, roles }: { children: React.ReactElement; roles?: string[] }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
}

function MasterAdminRoute({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'admin' || user.username !== 'admin') return <Navigate to="/admin/dashboard" />;
  return children;
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  switch (user.role) {
    case 'admin': return <Navigate to="/admin/dashboard" />;
    case 'employee': return <Navigate to="/employee/timesheet" />;
    case 'customer': return <Navigate to="/customer/timesheets" />;
    default: return <Navigate to="/login" />;
  }
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/" element={<RoleRedirect />} />

      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        {/* Admin routes */}
        <Route path="/admin/dashboard" element={<ProtectedRoute roles={['admin']}><AdminDashboardPage /></ProtectedRoute>} />
        <Route path="/admin/employees" element={<ProtectedRoute roles={['admin']}><AdminEmployeesPage /></ProtectedRoute>} />
        <Route path="/admin/admins" element={<MasterAdminRoute><AdminAdminsPage /></MasterAdminRoute>} />
        <Route path="/admin/clients" element={<ProtectedRoute roles={['admin']}><AdminClientsPage /></ProtectedRoute>} />
        <Route path="/admin/timesheets" element={<ProtectedRoute roles={['admin']}><AdminTimesheetsPage /></ProtectedRoute>} />
        <Route path="/admin/projects" element={<ProtectedRoute roles={['admin']}><AdminProjectsPage /></ProtectedRoute>} />
        <Route path="/admin/reports" element={<ProtectedRoute roles={['admin']}><AdminReportsPage /></ProtectedRoute>} />
        <Route path="/admin/expenses" element={<ProtectedRoute roles={['admin']}><AdminExpensesPage /></ProtectedRoute>} />
        <Route path="/admin/activity-logs" element={<ProtectedRoute roles={['admin']}><AdminActivityLogsPage /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute roles={['admin']}><div>Settings (TODO)</div></ProtectedRoute>} />

        {/* Employee routes */}
        <Route path="/employee/timesheet" element={<ProtectedRoute roles={['employee']}><EmployeeTimesheetPage /></ProtectedRoute>} />

        {/* Customer routes */}
        <Route path="/customer/timesheets" element={<ProtectedRoute roles={['customer']}><CustomerTimesheetsPage /></ProtectedRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
